<template>
	<div
		ref="summaryEl"
		:style="summaryMaxHeight ? { maxHeight: summaryMaxHeight } : {}"
		class="p-4 pt-6 bg-surface-grey-light rounded-md w-full md:w-[100%] md:sticky md:top-24 md:flex md:flex-col md:overflow-hidden"
	>
		<!-- todo remove image on "tablet" size -->
		<!-- Counter row -->
		<div class="flex items-center justify-between pb-4 md:shrink-0">
			<div class="flex items-center gap-2">
				<p class="text-headings first-letter:uppercase text-sm font-normal leading-[21px] md:text-[20px] md:font-semibold md:leading-tight">
					{{ doorsaleStore.selectedCount === 1 ? $t('doorsale.selected_events_one') : $t('doorsale.selected_events_other') }}
				</p>
				<span class="inline-flex items-center justify-center w-[22px] pb-[1px] md:w-8 md:h-8 rounded-full bg-primary text-primary-text text-sm font-semibold leading-[21px] md:text-[20px] md:leading-none">
					{{ doorsaleStore.selectedCount }}
				</span>
			</div>
			<div class="flex items-center gap-3">
				<button
					v-if="doorsaleStore.selectedCount > 0"
					type="button"
					class="text-text text-xs font-normal leading-5 underline hover:text-headings"
					@click="$emit('remove-all')"
				>
					{{ $t('doorsale.remove_all') }}
				</button>
				<!-- Mobile collapse toggle -->
				<button
					type="button"
					class="md:hidden inline-flex items-center gap-1 h-8 pt-[3px] pr-[10px] pb-[1px] pl-[15px] rounded-[22px] border border-surface-grey bg-surface-light"
					@click="listExpanded = !listExpanded"
					:aria-expanded="listExpanded"
				>
					<span class="text-headings text-xs font-normal leading-5">{{ listExpanded ? $t('doorsale.hide_list') : $t('doorsale.show_list') }}</span>
					<DropdownIcon
						class="h-3 w-3 transition-transform duration-200 fill-current"
						:class="{ 'rotate-180': listExpanded }"
					/>
				</button>
			</div>
		</div>

		<!-- Selected events list -->
		<div
			class="mt-4 space-y-4 overflow-y-auto max-h-[600px] md:shrink md:min-h-0 md:max-h-none"
			:class="{ 'hidden md:block': !listExpanded }"
		>
			<template
				v-for="eventId in doorsaleStore.selectedEventIds"
				:key="eventId"
			>
				<DoorsaleEventSummaryItem
					v-if="events.find(e => e.id === eventId)"
					:event="events.find(e => e.id === eventId)!"
					@remove="$emit('remove', eventId)"
				/>
			</template>

			<div v-if="doorsaleStore.selectedCount === 0" class="text-center text-text text-sm py-2">
				{{ $t('doorsale.no_events_selected') }}
			</div>
		</div>

		<!-- Continue button -->
		<div class="mt-6 md:flex md:justify-center md:items-center md:py-10 md:px-4 md:rounded-md md:border md:border-surface-grey md:bg-surface-light md:shrink-0">
			<BaseButton
				class="w-full"
				color="primary"
				size="lg"
				:disabled="doorsaleStore.selectedCount === 0"
				:class="doorsaleStore.selectedCount === 0 ? 'cursor-not-allowed opacity-50 pointer-events-none' : ''"
				@click="handleContinue"
			>
				Fortsæt
				<ArrowRightIcon class="fill-current h-4 w-4 ml-4" />
			</BaseButton>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { nextTick, onMounted, onUnmounted, ref } from 'vue';
	import { useI18n } from 'vue-i18n';
	import { useRouter } from 'vue-router';

	import DoorsaleEventSummaryItem from '@/components/doorsale/DoorsaleEventSummaryItem.vue';
	import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue';
	import DropdownIcon from '@/components/icons/DropdownIcon.vue';
	import BaseButton from '@/components/ui/BaseButton.vue';
	import { useDoorsaleStore } from '@/stores/doorsaleStore';
	import type { DoorsaleEvent } from '@/types';

	const { t: $t } = useI18n();

	defineEmits<{ remove: [eventId: number | string]; 'remove-all': [] }>();

	defineProps<{
		events: DoorsaleEvent[];
	}>();

	const doorsaleStore = useDoorsaleStore();
	const router = useRouter();

	// Mobile: list collapsed by default
	const listExpanded = ref(false);

	const summaryEl = ref<HTMLElement | null>(null);
	const summaryMaxHeight = ref<string | null>(null);
	let bodyResizeObserver: ResizeObserver | null = null;

	/**
	 * Recalculates the summary's max-height based on its current viewport position.
	 * Called on mount, on every body resize (catches async layout shifts from data loading),
	 * and on window resize.
	 *
	 * NOTE: The root element uses `md:overflow-hidden` to enforce this constraint.
	 * Any absolutely-positioned children (tooltips, dropdowns) that need to render
	 * outside the summary card will be clipped and must be teleported to a higher DOM node.
	 */
	function updateSummaryMaxHeight() {
		if (!summaryEl.value || window.innerWidth < 768) {
			summaryMaxHeight.value = null;
			return;
		}
		const top = summaryEl.value.getBoundingClientRect().top;
		summaryMaxHeight.value = `${Math.max(430, window.innerHeight - top - 10)}px`;
	}

	onMounted(() => {
		nextTick(() => requestAnimationFrame(updateSummaryMaxHeight));
		bodyResizeObserver = new ResizeObserver(updateSummaryMaxHeight);
		bodyResizeObserver.observe(document.body);
		window.addEventListener('resize', updateSummaryMaxHeight);
	});

	onUnmounted(() => {
		bodyResizeObserver?.disconnect();
		window.removeEventListener('resize', updateSummaryMaxHeight);
	});

	function handleContinue() {
		if (doorsaleStore.selectedCount === 0) return;
		router.push({ name: 'doorsale-tickets', params: { eventId: doorsaleStore.selectedEventIds[0] } });
	}
</script>
